import React, { useEffect, useState } from 'react';
import '../locales/i18n';
import '../utils/highlight';
import PropTypes from 'prop-types';
import Head from 'next/head';
import App from 'next/app';
import ThemeProvider from '../theme';
import { ModalStack } from '@mattjennings/react-modal-stack';
import * as Sentry from '@sentry/nextjs';

import NotistackProvider from '../components/NotistackProvider';
import { ColorContextProvider } from '@/theme/colorContext';
import { wrapper } from '../store';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useRouter } from 'next/router';
import Analytics from '../sections/analytics';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { poppins } from '@/theme/utils';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';

import '../styles/globals.css';

import { Loader } from '@/hooks/useCallApiProcess';
import { Backdrop } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SessionProvider } from '@/contexts/session/session';
import { RecaptchaProvider } from '@/contexts/recaptcha/RecaptchaContext';
import { isEmbeddedInWallet } from '@/utils/web-view';

const disabledPosthog = process.env.NEXT_PUBLIC_DISABLED_POSTHOG
  ? process.env.NEXT_PUBLIC_DISABLED_POSTHOG === 'true'
  : false;

// Check that PostHog is client-side (used to handle Next.js SSR)
if (typeof window !== 'undefined' && !disabledPosthog) {
  const urlParams = new URLSearchParams(window.location.search);
  const phDistinctId = urlParams.get('ph_distinct_id');
  const phSessionId = urlParams.get('ph_session_id');

  const bootstrapConfig = {};
  if (phDistinctId) bootstrapConfig.distinctID = phDistinctId;
  if (phSessionId) bootstrapConfig.sessionID = phSessionId;

  posthog.init(
    isEmbeddedInWallet()
      ? process.env.NEXT_PUBLIC_EMBEDDED_POSTHOG_KEY
      : process.env.NEXT_PUBLIC_POSTHOG_KEY,
    {
      api_host:
        process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com',
      bootstrap:
        Object.keys(bootstrapConfig).length > 0 ? bootstrapConfig : undefined,
      // Enable debug mode in development
      loaded: (posthog) => {
        if (process.env?.NEXT_PUBLIC_ENV === 'development') posthog.debug();
      },

      autocapture: {
        css_selector_allowlist: ['[ph-autocapture]'], // List of CSS selectors
      },
      capture_pageview: false,
      capture_pageleave: false,
    },
  );
}

MyApp.propTypes = {
  Component: PropTypes.func,
  pageProps: PropTypes.object,
};

export function MyApp(props) {
  const { t } = useTranslation();
  const { Component, pageProps } = props;
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [open, setOpen] = useState(false);

  const getLayout = Component.getLayout ?? ((page) => page);

  const router = useRouter();
  const { pageview } = useAnalyticsEvents();

  const handleViewHomeFirstTime = () => {
    pageview('/');
  };

  const handleRouteChange = (url) => {
    pageview(url);
  };

  useEffect(() => {
    if (isFirstTime) {
      handleViewHomeFirstTime();
      setIsFirstTime(false);
    }
  }, [isFirstTime]);

  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);

  useEffect(() => {
    const handleStart = () => {
      setOpen(true);
    };
    const handleStop = () => {
      setOpen(false);
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router]);

  const usePostHogProvider = !disabledPosthog ? (
    <PostHogProvider client={posthog}>
      {getLayout(<Component {...pageProps} />)}
    </PostHogProvider>
  ) : (
    <>{getLayout(<Component {...pageProps} />)}</>
  );

  // Skip reCAPTCHA providers on survey page when embedded in wallet.
  // The survey page sets security tokens directly in Redux (see useSurveyPage.js),
  // so it doesn't need reCAPTCHA v2 (RecaptchaProvider) or v3 (GoogleReCaptchaProvider).
  // Loading the Google reCAPTCHA script in a wallet webview without internet
  // access to Google's servers causes a connection error.
  const isSurveyPage = router.pathname === '/survey';

  const appContent = (
    <ColorContextProvider>
      <ThemeProvider>
        <NotistackProvider>
          <ModalStack>
            <Analytics />
            <Backdrop open={open}>
              <Loader msg={t('checkout.loadings.pleaseWait')} />
            </Backdrop>
            {usePostHogProvider}
          </ModalStack>
        </NotistackProvider>
      </ThemeProvider>
    </ColorContextProvider>
  );

  const body = isSurveyPage ? (
    appContent
  ) : (
    <RecaptchaProvider>{appContent}</RecaptchaProvider>
  );

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
        <style jsx global>{`
          html {
            font-family: ${poppins.style.fontFamily};
          }
        `}</style>
      </Head>
      <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
        {(process.env.NEXT_PUBLIC_USE_SECURITY === 'true' ||
          process.env.NEXT_PUBLIC_USE_RECAPTCHA_OTP === 'true') &&
          isSurveyPage && (
            <SessionProvider
              initialSessionToken={pageProps.sessionToken}
              subdomain={pageProps.subDomain}
            >
              {body}
            </SessionProvider>
          )}
        {(process.env.NEXT_PUBLIC_USE_SECURITY === 'true' ||
          process.env.NEXT_PUBLIC_USE_RECAPTCHA_OTP === 'true') &&
          !isSurveyPage && (
            <GoogleReCaptchaProvider
              reCaptchaKey={process.env.NEXT_PUBLIC_RE_CAPTCHA_KEY}
            >
              <SessionProvider
                initialSessionToken={pageProps.sessionToken}
                subdomain={pageProps.subDomain}
              >
                {body}
              </SessionProvider>
            </GoogleReCaptchaProvider>
          )}
        {process.env.NEXT_PUBLIC_USE_SECURITY !== 'true' &&
          process.env.NEXT_PUBLIC_USE_RECAPTCHA_OTP !== 'true' && (
            <SessionProvider
              initialSessionToken={pageProps.sessionToken}
              subdomain={pageProps.subDomain}
            >
              {body}
            </SessionProvider>
          )}
      </Sentry.ErrorBoundary>
    </>
  );
}

MyApp.getInitialProps = async (context) => {
  const appProps = await App.getInitialProps(context);

  return {
    ...appProps,
  };
};

export default wrapper.withRedux(MyApp);
